import React from 'react';

import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {SubTitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {WeekdayPicker} from '@spaced-out/ui-design-system/lib/components/WeekdayPicker';

import {
  selectCanNavigateNext,
  selectCanNavigatePrevious,
  selectWeekRangeDisplay,
  selectWeekDays,
  selectSelectedDate,
} from '../../selectors/booking-selector';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {useIsMobile} from '../../hooks/useIsMobile';

import css from './booking-week-day-picker.css';


export const BookingWeekDayPicker = () => {
  const {t, language} = useLabelTranslation();
  const isMobile = useIsMobile();
  const {bookingActor} = useBookingMachine();
  const canNavigateNext = useSelector(bookingActor, selectCanNavigateNext);
  const canNavigatePrevious = useSelector(
    bookingActor,
    selectCanNavigatePrevious,
  );
  const selectedDayForPicker = useSelector(bookingActor, (state) =>
    selectSelectedDate(state, language),
  );

  const weekRangeDisplay = useSelector(bookingActor, (state) =>
    selectWeekRangeDisplay(state, language),
  );
  const {weekDayOptions, disabledWeekDays} = useSelector(
    bookingActor,
    (state) => selectWeekDays(state, language),
  );

  const handleSelectDay = (weekDay: any) => {
    bookingActor.send({type: 'date.change', date: weekDay[0].key});
  };

  const handleNavigatePrevious = () => {
    bookingActor.send({type: 'week.prev'});
  };

  const handleNavigateNext = () => {
    bookingActor.send({type: 'week.next'});
  };

  return (
    <div className={css.weekNavigationContainer}>
      <div className={css.weekNavigation}>
        <Tooltip body={t('Previous', 'Previous')}>
          <Button
            type="ghost"
            iconLeftName="angle-left"
            onClick={handleNavigatePrevious}
            disabled={!canNavigatePrevious}
            aria-label="Navigate to previous week"
          />
        </Tooltip>
        {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
        <SubTitleMedium>{weekRangeDisplay}</SubTitleMedium>
        <Tooltip body={t('Next', 'Next')}>
          <Button
            type="ghost"
            iconLeftName="angle-right"
            onClick={handleNavigateNext}
            disabled={!canNavigateNext}
            aria-label="Navigate to next week"
          />
        </Tooltip>
      </div>
      <WeekdayPicker
        key={selectedDayForPicker?.key}
        size={isMobile ? 'small' : 'medium'}
        disableMultiSelect={true}
        customDayLabels={weekDayOptions}
        disabledWeekDays={disabledWeekDays}
        onChange={handleSelectDay}
        classNames={{
          wrapper: css.weekDayPickerWrapper,
          selectedButton: css.weekDayPickerSelectedButton,
        }}
        selectedWeekDays={selectedDayForPicker ? [selectedDayForPicker] : []}
      />
    </div>
  );
};
